import { TIME_FORMAT_SHORT } from '@/constants/dateTimeFormats';
import { initialState } from '../helpers/create_session_initial_state';
import { isScRelatedSession, isPaddlingSession } from '@/helpers/sessionHelper';
import { DateTime } from 'luxon';

export const createSession = {
    state: initialState(),

    getters: {
        recentVenues(state) {
            return state.recentVenues;
        },

        clubExercises(state) {
            return _.sortBy(state.exercises, 'name');
        },

        sessionDetails(state) {
            return state.details;
        },

        newSessionPreparedData(state, getters, rootState, rootGetters) {
            const data = {};

            // Details
            data.session_id = state.details.session_id;
            data.sport = state.details.sessionType;
            data.all_day_event = state.details.all_day_event;
            data.favorite = state.details.favorite;

            const { startDate, startTime, endDate, endTime } = state.details;
            data.start_date = `${DateTime.fromISO(startDate).toISODate()} ${DateTime.fromISO(startTime).toFormat(
                TIME_FORMAT_SHORT
            )}`;
            data.end_date = `${DateTime.fromISO(endDate).toISODate()} ${DateTime.fromISO(endTime).toFormat(
                TIME_FORMAT_SHORT
            )}`;

            data.name = state.details.name || '';
            data.description = state.details.description || '';
            data.weather = state.details.weather || '';
            data.planned_distance = state.details.totalDistance;
            data.planned_time = state.details.totalTime;
            data.attendance_limit = state.details.attendance_limit;
            data.planned_rpe = state.details.planned_rpe;

            Vue.set(data, 'repeat_frequency_type', null);

            if (state.details.repeat.checked) {
                _.each(state.details.repeat, (value, key) => {
                    if (key !== 'checked' && key !== 'editing') {
                        Vue.set(data, key, value);
                    }

                    if (key === 'repeat_frequency_type' && !value) {
                        Vue.set(data, key, null);
                    }

                    if (key === 'repeat_end_date') {
                        Vue.set(data, key, DateTime.fromISO(value).toISODate());
                    }
                });

                data.repeat_on_the_specific_days = state.details.repeat.repeat_on_the_specific_days;
            }

            if (data.repeat_frequency_type === 'specific_dates') {
                data.repeat_end_date = null;
            }

            if (!isScRelatedSession(data.sport)) {
                data.intensities = {};
                data.intensity_percentages = {};
                state.details.intensities.forEach(i => {
                    Vue.set(data.intensities, i.id, 0);
                    Vue.set(data.intensity_percentages, i.id, 0);
                    if (i.selected) {
                        Vue.set(data.intensities, i.id, 1);
                        Vue.set(data.intensity_percentages, i.id, i.percentage);
                    }
                });
            }

            data.stroke_rate_intensities = {};
            const strokeRateIntensities = isPaddlingSession(data.sport)
                ? state.details.stroke_rate_intensities_paddling
                : state.details.stroke_rate_intensities_rowing;
            strokeRateIntensities.forEach(i =>
                Vue.set(data.stroke_rate_intensities, i.id, i.selected ? i.percentage : 0)
            );

            data.heart_rate_intensities = {};
            state.details.heart_rate_intensities.forEach(i => {
                Vue.set(data.heart_rate_intensities, i.id, i.selected ? i.percentage : 0);
            });

            data.focuses = {};
            rootGetters['clubHelper/clubFocuses'].forEach(o => {
                Vue.set(data.focuses, o.id, state.details.focuses.find(({ id }) => id === o.id) ? 1 : 0);
            });

            _.each(state.details.location, (value, key) => {
                key !== 'id' && Vue.set(data, key, value);
            });

            data.tags = state.details.tags;

            // Attendance & Permissions
            data.athletes = state.attendance.athletes;
            data.coaches = state.attendance.coaches;
            data.athlete_permissions = state.attendance.athlete_permissions;
            data.coach_permissions = state.attendance.coach_permissions;

            // Structures
            data.structures = [];
            data.seat_racing = state.seat_racing;

            if (!isScRelatedSession(state.details.sessionType) && state.work.createPieces) {
                data.structures.push({});
                data.structures[0].sport = data.sport;

                let structure_key = 1;
                _.each(state.work.pieceTypes, (value, key) => {
                    if (value === true) {
                        switch (key) {
                            case 'single':
                                structure_key = 1;
                                break;
                            case 'multiple':
                                structure_key = 2;
                                break;
                            case 'variable':
                                structure_key = 3;
                                break;
                        }
                        data.structures[0].piece_structure = structure_key;
                    }
                });

                data.structures[0].pieces = {};

                if (structure_key !== 3) {
                    // if it's not Variable, take only 1st element
                    state.work.pieces.splice(1);
                }

                if (state.work.repetitions !== null) {
                    data.structures[0].repetitions = Number(state.work.repetitions);
                }
                if (state.details.totalDistance) {
                    data.structures[0].planned_distance = state.details.totalDistance;
                }
                if (state.details.totalTime) {
                    data.structures[0].planned_time = state.details.totalTime;
                }

                _.each(state.work.pieces, (piece, pieceIndex) => {
                    data.structures[0].pieces[pieceIndex] = {};

                    if (piece.distance !== null) {
                        data.structures[0].pieces[pieceIndex].planned_distance = Number(piece.distance);
                    }

                    if (piece.time !== null) {
                        data.structures[0].pieces[pieceIndex].planned_time = piece.time;
                    }

                    data.structures[0].pieces[pieceIndex].split_length = piece.split_length;

                    data.structures[0].pieces[pieceIndex].type_of_split_length = piece.variable_split_lengths.length
                        ? 2
                        : 1;

                    if (piece.rest !== null) {
                        data.structures[0].pieces[pieceIndex].rest = piece.rest;
                    }

                    data.structures[0].pieces[pieceIndex].segments = [];

                    _.each(piece.variable_split_lengths, (length, key) => {
                        data.structures[0].pieces[pieceIndex].segments[key] = {
                            length: length
                        };
                    });
                });
            } else if (isScRelatedSession(state.details.sessionType)) {
                // S&C related
                _.each(state.work.scStructures, (structure, structIndex) => {
                    data.structures[structIndex] = {
                        sport: data.sport,
                        name: structure.name,
                        athletes: structure.athletes,
                        pieces: {}
                    };

                    if (state.work.repetitions !== null) {
                        data.structures[structIndex].repetitions = Number(state.work.repetitions);
                    }
                    if (state.details.totalDistance) {
                        data.structures[structIndex].planned_distance = state.details.totalDistance;
                    }
                    if (state.details.totalTime) {
                        data.structures[structIndex].planned_time = state.details.totalTime;
                    }

                    _.each(structure.exercises, (exercise, exerciseIndex) => {
                        data.structures[structIndex].pieces[exerciseIndex] = {
                            exercise_id: exercise.exercise_id,
                            sets_count: exercise.sets_count
                        };

                        if (exercise.reps_count && exercise.reps_count !== 'custom') {
                            data.structures[structIndex].pieces[exerciseIndex].type_of_split_length = 1;
                            data.structures[structIndex].pieces[exerciseIndex].reps_count = exercise.reps_count;
                        } else {
                            data.structures[structIndex].pieces[exerciseIndex].type_of_split_length = 2;
                            data.structures[structIndex].pieces[exerciseIndex].segments = {};

                            if (exercise.segments) {
                                for (let i = 0; i < exercise.sets_count; i++) {
                                    data.structures[structIndex].pieces[exerciseIndex].segments[i] = {
                                        reps_count: exercise.segments[i]
                                    };
                                }
                            }
                        }
                    });
                });
            }

            return data;
        },

        newEventPreparedData(state, getters) {
            const data = getters.newSessionPreparedData;

            // We want these properties excluded when creating an event
            const excludedProperties = [
                'structures',
                'sport',
                'focuses',
                'intensities',
                'intensity_percentages',
                'heart_rate_intensities',
                'stroke_rate_intensities'
            ];
            return _.pickBy(data, (val, key) => !excludedProperties.includes(key));
        }
    },

    mutations: {
        resetWorkStructure(state) {
            state.work = {
                createPieces: false,
                repetitions: null,
                pieceTypes: {
                    single: true,
                    multiple: false,
                    variable: false
                },
                pieces: [
                    {
                        distance: null,
                        time: null,
                        split_length: null,
                        variable_split_lengths: [],
                        rest: null
                    }
                ],
                scStructures: []
            };
        },

        resetWorkDetails(state) {
            state.details.totalDistance = '';
            state.details.totalTime = '';
            state.details.focuses = [];
        },

        resetRepeatStructure(state) {
            state.details.repeat = {
                checked: null,
                editing: false,
                repeat_frequency_type: null,
                repeat_every: 1,
                repeat_on_the_specific_step: null,
                repeat_on_the_specific_every: null,
                repeat_end_date: null,
                repeat_numbers: null,
                repeat_on_the_specific_days: []
            };
        },

        newSessionIntensities(state, data) {
            state.details.intensities = data.map(({ id, name, color, order }) => ({
                id,
                name,
                color,
                order,
                percentage: 0,
                selected: false
            }));
        },
        newSessionStrokeRateIntensitiesPaddling(state, data) {
            state.details.stroke_rate_intensities_paddling = data.map(({ id, name, color }) => ({
                id,
                name,
                color,
                percentage: 0,
                selected: false
            }));
        },
        newSessionStrokeRateIntensitiesRowing(state, data) {
            state.details.stroke_rate_intensities_rowing = data.map(({ id, name, color }) => ({
                id,
                name,
                color,
                percentage: 0,
                selected: false
            }));
        },
        newSessionHeartRateIntensities(state, data) {
            state.details.heart_rate_intensities = data.map(({ id, name, color }) => ({
                id,
                name,
                color,
                percentage: 0,
                selected: false
            }));
        },
        clubExercises(state, data) {
            state.exercises = [...data];
        },
        setSession(state, data) {
            state = data;
        },
        updateSessionType(state, type) {
            state.details.sessionType = type;
        },
        setRecentVenues(state, data) {
            state.recentVenues = [];
            data.forEach(venue => {
                const { id, name, formatted_address, latitude, longitude } = venue;
                state.recentVenues.push({
                    id,
                    name,
                    formatted_address,
                    lat: latitude && Number(latitude),
                    lng: longitude && Number(longitude)
                });
            });
        },
        addVenue(state, data) {
            const index = state.recentVenues.findIndex(({ id }) => id === data.id);
            index !== -1 && state.recentVenues.splice(index, 1);
            state.recentVenues.push(data);
        },
        removeVenue(state, argId) {
            const index = state.recentVenues.findIndex(({ id }) => id === argId);
            index !== -1 && state.recentVenues.splice(index, 1);
        }
    },

    actions: {
        getExercises(context, payload) {
            return context.dispatch('get', {
                url: api + 'exercises',
                params: payload
            });
        },
        createExercise(context, payload) {
            return context.dispatch('post', {
                url: api + 'exercises/add',
                data: payload
            });
        },
        createSession(context, payload) {
            return context.dispatch('post', {
                url: api + 'create-session',
                data: payload
            });
        },
        createEvent(context, payload) {
            return context.dispatch('post', {
                url: api + 'create-event',
                data: payload
            });
        },
        newSessionIntensities({ commit }, payload) {
            return commit('newSessionIntensities', payload);
        },
        newSessionStrokeRateIntensitiesPaddling({ commit }, payload) {
            return commit('newSessionStrokeRateIntensitiesPaddling', payload);
        },
        newSessionStrokeRateIntensitiesRowing({ commit }, payload) {
            return commit('newSessionStrokeRateIntensitiesRowing', payload);
        },
        newSessionHeartRateIntensities({ commit }, payload) {
            return commit('newSessionHeartRateIntensities', payload);
        },
        resetIntensities(
            { commit },
            { intensities, stroke_rate_intensities_paddling, stroke_rate_intensities_rowing, heart_rate_intensities }
        ) {
            commit('newSessionIntensities', intensities);
            commit('newSessionStrokeRateIntensitiesPaddling', stroke_rate_intensities_paddling);
            commit('newSessionStrokeRateIntensitiesRowing', stroke_rate_intensities_rowing);
            commit('newSessionHeartRateIntensities', heart_rate_intensities);
        }
    }
};

export default createSession;
